<script setup lang="ts">
  import { computed, nextTick, onMounted, ref } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import { useModal } from '@/composition/use-modal';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { normalizeText, splitTerms } from '@/domain/pos/search-normalize';
  import { formatMoney } from '@/domain/store-setup/money';
  import type { ILiveCategory, ILiveOther } from '@/interfaces/IMenuImport';
  import { nextDomId } from '@/utils/dom-id.utils';

  /**
   * «Elija el plato de su menú» (plan §3.5 «Picker»): the store's dishes no
   * dish of the list is linked to, by category, with a search. Opened with
   * `showDynamicDialog<number | null>(…, 'v2-menu-live-picker')`; resolves
   * with the picked dish's id, or null when closed.
   */
  const props = defineProps<{
    others: ILiveOther[];
    categories: ILiveCategory[];
    currency: string;
    country: string;
  }>();
  const emit = defineEmits<{ (e: 'close'): void }>();
  const modals = useModal();
  const { width } = useStableWindowSize();

  /** Wide screens get the search focused; phones keep the keyboard closed. */
  const AUTOFOCUS_MIN_WIDTH = 1024;
  const searchId = nextDomId('menu-live-picker-search');
  const query = ref('');

  interface IPickerGroup {
    key: string;
    name: string | null;
    dishes: { other: ILiveOther; folded: string }[];
  }

  /** The dishes by category in store order; each name folded once for the search. */
  const groups = computed<IPickerGroup[]>(() => {
    const folded = props.others.map((other) => ({
      other,
      folded: normalizeText(other.name),
    }));
    const known = new Set(props.categories.map((category) => category.id));
    const list: IPickerGroup[] = props.categories.map((category) => ({
      key: `c${category.id}`,
      name: category.name,
      dishes: folded.filter((entry) => entry.other.categoryId === category.id),
    }));
    list.push({
      key: 'rest',
      name: null,
      dishes: folded.filter((entry) => !known.has(entry.other.categoryId)),
    });
    return list.filter((group) => group.dishes.length > 0);
  });

  /** Every typed term is part of the dish name (accents and case aside). */
  const shown = computed(() => {
    const terms = splitTerms(query.value);
    if (terms.length === 0) return groups.value;
    return groups.value
      .map((group) => ({
        ...group,
        dishes: group.dishes.filter((entry) =>
          terms.every((term) => entry.folded.includes(term))
        ),
      }))
      .filter((group) => group.dishes.length > 0);
  });

  function money(value: number): string {
    return formatMoney(value, props.currency, props.country);
  }

  function pick(liveItemId: number) {
    void modals.dialogSubmit('v2-menu-live-picker', liveItemId);
  }

  onMounted(async () => {
    if (width.value < AUTOFOCUS_MIN_WIDTH) return;
    await nextTick();
    const input = document.getElementById(searchId);
    if (input instanceof HTMLInputElement) input.focus();
  });
</script>

<template>
  <div class="v2-menu-live-picker">
    <header class="picker-head">
      <h2 class="picker-title">{{ $t('menuImport.update.pickerTitle') }}</h2>
      <PosUiInput
        :id="searchId"
        :value="query"
        icon="magnify"
        clearable
        autocomplete="off"
        :placeholder="$t('menuImport.update.pickerSearch')"
        :aria-label="$t('menuImport.update.pickerSearch')"
        @input="query = $event"
      />
    </header>
    <div class="picker-body">
      <PosUiEmptyState
        v-if="shown.length === 0"
        icon="magnify"
        :title="$t('menuImport.update.pickerEmpty')"
      />
      <section v-for="group in shown" :key="group.key" class="picker-group">
        <h3 v-if="group.name !== null" class="group-name">{{ group.name }}</h3>
        <ul class="group-list">
          <li v-for="{ other } in group.dishes" :key="other.itemId">
            <button type="button" class="dish-row" @click="pick(other.itemId)">
              <span class="dish-text">
                <span class="dish-name">{{ other.name }}</span>
                <PosUiBadge v-if="!other.visible" size="sm">{{
                  $t('menuImport.update.hiddenInMenu')
                }}</PosUiBadge>
              </span>
              <span class="dish-price">{{ money(other.price) }}</span>
              <PosUiIcon name="chevron-right" class="dish-caret" />
            </button>
          </li>
        </ul>
      </section>
    </div>
    <footer class="picker-foot">
      <PosUiButton color="neutral" variant="outline" @click="emit('close')">{{
        $t('menuImport.update.undoCancel')
      }}</PosUiButton>
    </footer>
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-live-picker {
    // A fixed height: the dialog never jumps while the search filters the list.
    @apply tw-flex tw-flex-col tw-bg-white;
    height: min(85vh, 36rem);

    .picker-head {
      @apply tw-flex tw-shrink-0 tw-flex-col tw-gap-3 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-4;
    }
    .picker-title {
      @apply tw-m-0 tw-text-lg tw-font-semibold tw-text-neutral-900;
    }
    .picker-body {
      @apply tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-gap-4 tw-overflow-y-auto tw-px-5 tw-py-4;
    }
    .picker-group {
      @apply tw-flex tw-flex-col tw-gap-1;
    }
    .group-name {
      @apply tw-m-0 tw-text-xs tw-font-bold tw-uppercase tw-tracking-wide tw-text-neutral-500;
    }
    .group-list {
      @apply tw-m-0 tw-flex tw-list-none tw-flex-col tw-p-0;
    }
    .dish-row {
      @apply tw-flex tw-min-h-12 tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-2 tw-py-2 tw-text-left hover:tw-bg-neutral-100;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .dish-text {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1;
    }
    .dish-name {
      @apply tw-min-w-0 tw-break-words tw-text-sm tw-font-medium tw-text-neutral-900;
    }
    .dish-price {
      @apply tw-shrink-0 tw-whitespace-nowrap tw-text-sm tw-font-semibold tw-tabular-nums tw-text-neutral-900;
    }
    .dish-caret {
      @apply tw-shrink-0 tw-text-xl tw-text-neutral-400;
    }
    .picker-foot {
      @apply tw-flex tw-shrink-0 tw-justify-end tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200 tw-px-5 tw-py-3;
    }
  }
</style>
